import { useCallback, useEffect, useState } from "react";
import { Dropdown, Modal } from "react-bootstrap";
import OwTitle from "@components/shared/OwTitle/OwTitle";
import { OwFnTable } from "@components/shared/OwFnTable/OwFnTable";
import { SearchSection } from "@components/shared/SearchSection/SearchSection";
import { PaginationBar } from "@components/shared/PaginationBar/PaginationBar";
import { ControlBar } from "@components/shared/ControlBar/ControlBar";
import { OwFnInput } from "@components/shared/OwFnInput/OwFnInput";
import Typeahead from "@components/shared/Typeahead/Typeahead";
import OwLabel from "@components/shared/OwLabel/OwLabel";
import OwButton from "@components/shared/OwButton/OwButton";
import { OwDeleteEditActions } from "@components/shared/OwDeleteEditActions/OwDeleteEditActions";
import { ConfirmDeleteOverlayFn } from "@components/shared/ConfirmDeleteOverlayFn/ConfirmDeleteOverlayFn";
import UIService from "@services/UIService";
import VendorService from "@services/VendorService";
import AccountNotesService from "@services/AccountNotesService";
import { buildRestUrlWithQuery, buildAuthHeadersForFetch } from "@services/RestService";
import RestEndpointsEnum from "@models/RestEndpointsEnum";
import IAccount_Note from "@models/IAccount_Note";
import IOwTableCols from "@models/IOwTableCols";
import IVendorModel from "@models/IVendorModel";
import SortOrderEnum from "@models/SortOrderEnum";
import { useAppContextNonNull } from "@contexts/AppContext";
import { AccountNotesCsvUploadModal } from "./AccountNotesCsvUploadModal";
import { AccountNoteSidebar } from "./AccountNoteSidebar";

type Props = {};

export const AccountNotes = (_props: Props) => {
    const { userModel, supplierModel, vendorModel, uiContext } = useAppContextNonNull();

    // Data
    const [notes, setNotes] = useState<IAccount_Note[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);

    // Fetch status
    const [isDataFetching, setIsDataFetching] = useState<boolean>(true);
    const [isInitialData, setIsInitialData] = useState<boolean>(true);

    // Search
    const [foundVendors, setFoundVendors] = useState<IVendorModel[]>([]);
    const [selectedVendor, setSelectedVendor] = useState<IVendorModel | null>(null);
    const [clearVendorTypeahead, setClearVendorTypeahead] = useState<boolean>(false);
    const [contact, setContact] = useState<string>("");

    // Sort
    const [sortCol, setSortCol] = useState<string>("created_at");
    const [sortOrder, setSortOrder] = useState<SortOrderEnum>(SortOrderEnum.DESC);

    // Pagination
    const [offset, setOffset] = useState<number>(0);
    const [limit, setLimit] = useState<number>(25);
    const [hasPaginationChange, setHasPaginationChange] = useState<boolean>(false);

    // Upload modal
    const [showUploadModal, setShowUploadModal] = useState<boolean>(false);

    // Delete confirmation
    const [deleteRow, setDeleteRow] = useState<string | null>(null);
    const [pendingDeleteNote, setPendingDeleteNote] = useState<IAccount_Note | null>(null);

    const fetchNotes = useCallback(async () => {
        try {
            setIsDataFetching(true);

            const query: any = {
                supplier_id: supplierModel.id,
                limit,
                page: offset,
                order_by: sortCol,
                order_dir: sortOrder,
                paginated: true,
            };

            if (selectedVendor) {
                query.vendor_id = selectedVendor.id;
            }

            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.AccountNotesGetAll,
                query,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
                vendorModel,
            });

            const response = await fetch(url, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch notes: ${response.statusText}`);
            }

            const data = (await response.json()) as { notes: IAccount_Note[]; totalCount: number };
            setNotes(data.notes || []);
            setTotalCount(data.totalCount || 0);
        } catch (error) {
            console.error("Error fetching account notes:", error);
            setNotes([]);
            setTotalCount(0);
        } finally {
            setIsDataFetching(false);
            setIsInitialData(false);
            setHasPaginationChange(false);
        }
    }, [userModel, supplierModel, vendorModel, offset, limit, sortCol, sortOrder, selectedVendor]);

    useEffect(() => {
        window.document.title = "Orderwerks >> Account Notes";
        fetchNotes();
    }, []);

    useEffect(() => {
        if (hasPaginationChange) {
            fetchNotes();
        }
    }, [hasPaginationChange]);

    useEffect(() => {
        fetchNotes();
    }, [sortCol, sortOrder]);

    const handleChange = (e: any) => {
        const value = e.target.value;

        switch (e.target.name) {
            case "contact":
                setContact(value);
                break;
        }
    };

    const findVendorsByName = async (typeahead: string) => {
        try {
            const data = await VendorService.getVendors({ name: typeahead, limit: "25" }, userModel, supplierModel);
            setFoundVendors(data.supplierVendorTokens.map((t) => t.Vendor!));
        } catch (error) {
            console.error("Error finding vendors:", error);
        }
    };

    const selectedVendorFn = (vendor: IVendorModel) => {
        setFoundVendors([]);
        setSelectedVendor(vendor);
    };

    const clearTypeaheadCallback = () => {
        setClearVendorTypeahead(false);
    };

    const enterSearch = (e: any) => {
        if (e.key === "Enter") {
            performSearch();
        }
    };

    const performSearch = () => {
        setOffset(0);
        fetchNotes();
    };

    const resetFilters = () => {
        setSelectedVendor(null);
        setClearVendorTypeahead(true);
        setContact("");
        setOffset(0);
        setLimit(25);
        setTotalCount(0);
        setNotes([]);
        fetchNotes();
    };

    const toggleSortDir = (_sortCol: string, _sortOrder: SortOrderEnum) => {
        setSortCol(_sortCol);
        setSortOrder(_sortOrder);
    };

    const paginationChanged = (_offset: number, _limit: number) => {
        setOffset(_offset);
        setLimit(_limit);
        setHasPaginationChange(true);
    };

    const downloadCsv = async () => {
        try {
            const url = buildRestUrlWithQuery({
                endpoint: RestEndpointsEnum.AccountNotesCsvTemplate,
            });

            const headers = buildAuthHeadersForFetch({
                userModel,
                supplierModel,
            });

            const response = await fetch(url, {
                method: "GET",
                headers,
            });

            if (!response.ok) {
                throw new Error(`Failed to download CSV: ${response.statusText}`);
            }

            const csvData = await response.text();
            UIService.downloadCSVData(csvData, "account-notes-template.csv");
        } catch (error) {
            console.error("Error downloading CSV template:", error);
        }
    };

    const openNoteSidebar = (note?: IAccount_Note) => {
        const sidebarComp = (
            <AccountNoteSidebar
                existingNote={note}
                closeSidebarFn={() => {
                    uiContext.toggleSidebar(false);
                }}
                successFn={() => {
                    uiContext.toggleSidebar(false);
                    fetchNotes();
                }}
            />
        );
        uiContext.setSidebarComponent(sidebarComp);
        uiContext.toggleSidebar(true);
    };

    const confirmDelete = (note: IAccount_Note, index: number) => {
        setPendingDeleteNote(note);
        setDeleteRow(`row-${index}`);
    };

    const cancelDelete = () => {
        setPendingDeleteNote(null);
        setDeleteRow(null);
    };

    const commitDelete = async () => {
        if (!pendingDeleteNote) return;

        try {
            await AccountNotesService.deleteAccountNote(pendingDeleteNote.id, userModel, supplierModel);
            fetchNotes();
        } catch (error) {
            console.error("Error deleting account note:", error);
        } finally {
            setPendingDeleteNote(null);
            setDeleteRow(null);
        }
    };

    const formatDate = (date: string | Date) => {
        if (!date) return "";
        return new Date(date).toLocaleDateString();
    };

    const columns: IOwTableCols[] = [
        {
            title: "",
            valueProp: "actions",
            renderFn: (row: any, index: number) => {
                return (
                    <OwDeleteEditActions
                        isEditMode={false}
                        onEditClickFn={() => openNoteSidebar(row as IAccount_Note)}
                        onTrashClickFn={() => confirmDelete(row as IAccount_Note, index)}
                    />
                );
            },
            renderFnEmitsRow: true,
        },
        {
            title: "vendor",
            valueProp: "Vendor",
            renderFn: (row: any) => {
                return row.Vendor?.name || "-";
            },
            renderFnEmitsRow: true,
            isVendorOverride: true,
            isSortable: true,
            sortCol: "vendor_name",
            sortOrderFn: () => sortOrder,
            sortFn: (_sortCol: string, _sortOrder: SortOrderEnum) => {
                toggleSortDir("vendor_name", _sortOrder);
            },
        },
        {
            title: "contact",
            valueProp: "Contact",
            renderFn: (row: any) => {
                return row.Contact?.name || "-";
            },
            renderFnEmitsRow: true,
            isSortable: true,
            sortCol: "contact_name",
            sortOrderFn: () => sortOrder,
            sortFn: (_sortCol: string, _sortOrder: SortOrderEnum) => {
                toggleSortDir("contact_name", _sortOrder);
            },
        },
        {
            title: "note",
            valueProp: "note",
        },
        {
            title: "created_at",
            valueProp: "created_at",
            renderFn: (row: any) => {
                return formatDate(row.created_at);
            },
            renderFnEmitsRow: true,
            isSortable: true,
            sortCol: "created_at",
            sortOrderFn: () => sortOrder,
            sortFn: (_sortCol: string, _sortOrder: SortOrderEnum) => {
                toggleSortDir("created_at", _sortOrder);
            },
        },
        {
            title: "updated_at",
            valueProp: "updated_at",
            renderFn: (row: any) => {
                return formatDate(row.updated_at);
            },
            renderFnEmitsRow: true,
            isSortable: true,
            sortCol: "updated_at",
            sortOrderFn: () => sortOrder,
            sortFn: (_sortCol: string, _sortOrder: SortOrderEnum) => {
                toggleSortDir("updated_at", _sortOrder);
            },
        },
    ];

    const csvActionsSection = userModel?.isSupplierUser ? (
        <Dropdown>
            <Dropdown.Toggle size="sm" variant="primary" id="csv-actions-dropdown">
                <i className="fas fa-file-csv me-2"></i>
                CSV
            </Dropdown.Toggle>
            <Dropdown.Menu className={UIService.isMainViewLight() ? "" : "bg-secondary"}>
                <Dropdown.Item className="bg-primary text-white" onClick={downloadCsv}>
                    <i className="fas fa-download me-2"></i>
                    Download CSV
                </Dropdown.Item>
                <Dropdown.Item className="bg-danger text-white" onClick={() => setShowUploadModal(true)}>
                    <i className="fas fa-upload me-2"></i>
                    Upload CSV
                </Dropdown.Item>
            </Dropdown.Menu>
        </Dropdown>
    ) : null;

    return (
        <div className={"AccountNotes pb-5 pb-md-0"}>
            <div className={"row mt-2"}>
                <div className={"col-12 d-flex flex-row align-items-center"}>
                    <div className={"d-flex align-items-center"}>
                        <OwTitle title={"notes"} leftIconName={"fad fa-sticky-note"} className={"me-auto"} />
                        <div className={"ms-2"}>
                            <OwButton
                                title={"create"}
                                fn={() => openNoteSidebar()}
                                className={"btn-warning btn-sm"}
                                leftIconClass={"fas fa-plus"}
                            />
                        </div>
                    </div>

                    <div className={"d-flex ms-auto d-print-none align-items-end mb-2 gap-2"}>
                        <ControlBar
                            sections={[
                                {
                                    name: "csv_actions",
                                    elements: [csvActionsSection],
                                },
                            ]}
                        />
                    </div>
                </div>
            </div>

            <div className={"mb-2"}>
                <SearchSection>
                    <div className={"row mb-2 align-items-end"}>
                        <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                            <OwLabel title={"vendor"} isSupplierVendorOverride={true} supplierModel={supplierModel} />
                            <div className="position-relative">
                                <Typeahead
                                    onKeyDownFn={enterSearch}
                                    title={"vendor search"}
                                    className="form-control-sm"
                                    clearTypeahead={clearVendorTypeahead}
                                    clearTypeaheadCallback={clearTypeaheadCallback}
                                    typeaheadItems={foundVendors}
                                    titleProp="name"
                                    subTitleProp=""
                                    maxResults={25}
                                    scrollableList={true}
                                    placeholder=""
                                    selectedItem={selectedVendor}
                                    isSelectableFn={() => true}
                                    selectedItemFn={selectedVendorFn}
                                    typeaheadApiFn={findVendorsByName}
                                />
                            </div>
                        </div>
                        {/* <div className={"mb-2 mb-md-0 col-12 col-sm-6 col-md-2"}>
                            <OwLabel title={"contact"} />
                            <OwFnInput fn={handleChange} name={"contact"} value={contact} onKeyDownFn={enterSearch} />
                        </div> */}
                        <div className={"col-auto"}>
                            <OwButton title={"search"} fn={performSearch} className={"btn-primary me-2"} />
                            <OwButton title={"reset"} fn={resetFilters} className={"btn-danger"} />
                        </div>
                    </div>
                </SearchSection>
            </div>

            <div className={"table-responsive position-relative"} id={"account-notes-table"}>
                <ConfirmDeleteOverlayFn
                    targetElem={deleteRow}
                    handleConfirm={commitDelete}
                    handleCancel={cancelDelete}
                    nestedParentElem={"account-notes-table"}
                    skipMainView={true}
                />
                <OwFnTable
                    columns={columns}
                    dataSet={notes}
                    dataFetch={isDataFetching}
                    initialData={isInitialData}
                    currentSortCol={sortCol}
                    wrappingClassName={"w-100"}
                />
            </div>

            {notes && notes.length > 0 ? (
                <PaginationBar offset={offset} limit={limit} totalRecords={totalCount} onPaginationChanged={paginationChanged} />
            ) : null}

            <Modal
                show={showUploadModal}
                onHide={() => setShowUploadModal(false)}
                size="lg"
                className="AccountNotesCsvUploadModal"
                centered>
                <Modal.Body>
                    <AccountNotesCsvUploadModal
                        closeModalFn={() => {
                            setShowUploadModal(false);
                            fetchNotes();
                        }}
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};
